import React, { useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { useQueries } from '@tanstack/react-query';
import { ButtonVariant } from '@patternfly/react-core';

import ContentPage from 'components/ContentView/ContentPage/ContentPage';
import useAlerts from 'utils/hooks/useAlerts';
import createQueryRequest from 'services/createQueryRequest';
import useAPI from 'services/useAPI';
import { ResourceType } from 'utils/types';
import { org2providers } from './queries';
import RelationTable from 'components/RelationTable/RelationTable';
import useRelations from 'utils/hooks/useRelations';
import { useRelationData, createRelationQueryData, createOrgOwnerRelationQueryData } from './queries';
import { providerKeys } from 'components/Admin/Providers/queries';
import { useTranslation } from 'localization/useTranslation';


const OrgToProvidersRelations = ({ breadcrumbs }) => {
  const api = useAPI();
  const T = useTranslation();
  const { orgId } = useParams();
  const history = useHistory();
  const [alerts,, addErrorAlert] = useAlerts();

  const [providersQuery, relationQuery, ownerQuery] = useQueries({
    queries: [
      { queryKey: providerKeys.providers(), queryFn: createQueryRequest(api.core.getProviders) },
      { queryKey: org2providers.org(createRelationQueryData(ResourceType.Provider, orgId)), queryFn: createQueryRequest(api.core.findRelationships) },
      { queryKey: org2providers.org(createOrgOwnerRelationQueryData(ResourceType.Provider)), queryFn: createQueryRequest(api.core.findRelationships) }
    ]
  });

  const providers = useRelationData({
    isLoading: providersQuery.isLoading || relationQuery.isLoading || ownerQuery.isLoading,
    resources: providersQuery.data,
    resourceRelations: relationQuery.data,
    ownedRelations: ownerQuery.data,
    resourceType: ResourceType.Provider
  });

  const { updateRelations, deleteRelations } = useRelations({
    refetchFn: relationQuery.refetch,
    queryKey: org2providers.org(createRelationQueryData(ResourceType.Provider)),
    onError: addErrorAlert
  });

  const rowActions = useMemo(() => {
    return [
      {
        variant: ButtonVariant.secondary,
        isOverflow: true,
        title: T.sidebar.item.providerClients,
        onClick: (row) => {
          history.push(`/admin/organizations/${orgId}/providers/${row.id}/provider_clients`);
        }
      }
    ];
  }, [T.sidebar.item.providerClients, history, orgId]);

  return (
    <ContentPage
      breadcrumb={breadcrumbs}
    >
      { alerts }
      <RelationTable
        orgId={orgId}
        resources={providers}
        isLoading={providersQuery.isLoading || relationQuery.isLoading || ownerQuery.isLoading}
        updateRelationsFn={updateRelations.mutate}
        deleteRelationsFn={deleteRelations.mutate}
        prependedRowActions={rowActions}
      />
    </ContentPage>
  );
};

export default OrgToProvidersRelations;
